(function (window) {
  "use strict";

  var DEFAULT_CONFIG = {};

  var state = {
    initialized: false,
    config: { ...DEFAULT_CONFIG },
    env: null, // { type: 'test' | 'live', checkoutOrigin: string }
    clientKey: null,
    fetchClientToken: null, // async () => Promise<string>
    fetchSessionStatus: null, // async (sessionId: string) => Promise<{ status: string, ... }>
    currentClientToken: null,
    modalEl: null,
    iframeEl: null,
    inlineEl: null,
    inlineContainer: null,
    messageListenerBound: false,
    callbacks: {
      onSuccess: null,
      onFailure: null,
      onCancel: null,
      onAttemptFailed: null,
      onExpired: null,
    },
  };

  function ensureInitialized() {
    if (!state.initialized) {
      throw new Error(
        "[Moment] SDK not initialized. Call Moment.init(...) first.",
      );
    }
  }

  function resolveEnvironment(clientKey) {
    if (typeof clientKey !== "string") {
      throw new Error("[Moment] clientKey must be a string");
    }

    if (clientKey.startsWith("pk_test_")) {
      return {
        type: "test",
        checkoutOrigin: "https://checkout-sandbox.momentpay.io",
      };
    }

    if (clientKey.startsWith("pk_live_")) {
      return {
        type: "live",
        checkoutOrigin: "https://checkout.momentpay.io",
      };
    }

    // You can relax this if you want custom prefixes, but stricter is better:
    throw new Error(
      "[Moment] Invalid clientKey format. Expected pk_test_... or pk_live_...",
    );
  }

  // ========== JWT DECODING & VALIDATION ==========
  // Decodes a JWS (unsigned JWT) token and returns the payload object.
  // Does NOT verify signature - assumes backend verification.
  function decodeJwt(token) {
    if (typeof token !== "string") {
      throw new Error("[Moment] clientToken must be a string");
    }
    var parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error(
        "[Moment] Invalid clientToken format: expected JWT with 3 parts",
      );
    }
    try {
      // Base64url decode the payload (2nd part)
      var payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      var decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (e) {
      throw new Error("[Moment] Failed to decode clientToken: " + e.message);
    }
  }

  // Validates that the JWT has not expired based on the `exp` claim.
  function assertTokenNotExpired(payload) {
    if (!payload.exp) {
      throw new Error("[Moment] clientToken missing exp claim");
    }
    var now = Date.now();
    var expMs = payload.exp * 1000;
    if (now > expMs) {
      throw new Error("[Moment] clientToken has expired");
    }
  }

  // ========== ANCESTOR ORIGINS ==========
  // Builds the ancestor origin chain that the checkout iframe will see.
  // This includes the current page (which will be the iframe's direct parent)
  // plus any ancestors if the current page is itself embedded.
  function getAncestorOrigins() {
    var origins = [];

    // Always include the current page's origin - this is the checkout iframe's direct parent
    var currentOrigin = window.location.origin;
    origins.push(currentOrigin);

    // Now check if WE are in an iframe and add our ancestors
    // Use ancestorOrigins API if available (Chrome, Edge)
    if (
      window.location.ancestorOrigins &&
      window.location.ancestorOrigins.length > 0
    ) {
      for (var i = 0; i < window.location.ancestorOrigins.length; i++) {
        origins.push(window.location.ancestorOrigins[i]);
      }
      return origins;
    }

    // Fallback for browsers without ancestorOrigins (Firefox, Safari)
    // Check if we're in an iframe
    var inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch (e) {
      // Access denied means we're in a cross-origin iframe
      inIframe = true;
    }

    if (!inIframe) {
      // Not in an iframe, just return current origin
      return origins;
    }

    // Try to traverse parent frames for same-origin ancestors
    try {
      var currentWindow = window;
      while (currentWindow !== currentWindow.parent) {
        currentWindow = currentWindow.parent;
        try {
          // Try to access origin (will fail cross-origin)
          origins.push(currentWindow.location.origin);
        } catch (e) {
          // Cross-origin frame - stop traversing
          break;
        }
      }
    } catch (e) {
      // Access denied
    }

    // If we're in an iframe but couldn't get parent origins, use document.referrer
    if (origins.length === 1 && document.referrer) {
      try {
        var url = new URL(document.referrer);
        // Only add if different from current origin
        if (url.origin !== currentOrigin) {
          origins.push(url.origin);
        }
      } catch (e) {
        // Invalid referrer, skip
      }
    }

    return origins;
  }

  // ========== API & SESSION STATUS ==========
  function getSessionIdFromUrl() {
    var urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("session_id");
  }

  function removeSessionIdFromUrl() {
    var url = new URL(window.location.href);
    url.searchParams.delete("session_id");
    window.history.replaceState({}, document.title, url.toString());
  }

  function handleSessionStatus(response) {
    var status = response.status;
    var payload = response;

    if (status === "completed" && state.callbacks.onSuccess) {
      state.callbacks.onSuccess(payload);
    } else if (status === "failed" && state.callbacks.onFailure) {
      state.callbacks.onFailure(payload);
    } else if (status === "expired" && state.callbacks.onExpired) {
      state.callbacks.onExpired(payload);
    } else if (status === "cancelled" && state.callbacks.onCancel) {
      state.callbacks.onCancel(payload);
    }
  }

  function createModalIfNeeded() {
    if (state.modalEl) return;

    var overlay = document.createElement("div");
    overlay.id = "moment-collect-overlay";
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
    overlay.style.display = "none";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.zIndex = "9999";

    var container = document.createElement("div");
    container.id = "moment-collect-modal";
    container.style.position = "relative";
    container.style.width = "100%";
    container.style.maxWidth = "430px";
    container.style.backgroundColor = "#fff";
    container.style.borderRadius = "24px";
    container.style.overflow = "hidden";
    container.style.boxShadow = "0 12px 30px rgba(126, 103, 103, 0.2)";

    var iframe = document.createElement("iframe");
    iframe.id = "moment-collect-iframe";
    iframe.style.width = "100%";
    iframe.style.height = "97vh";
    iframe.style.border = "none";
    iframe.style.display = "block";
    iframe.setAttribute("allow", "payment *;");

    container.appendChild(iframe);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    state.modalEl = overlay;
    state.iframeEl = iframe;
  }

  function openModal(checkoutUrl, clientToken) {
    createModalIfNeeded();

    state.iframeEl.src = buildEmbedUrl(checkoutUrl);
    state.modalEl.style.display = "flex";
    state.currentClientToken = clientToken;

    setupMessageListener();
  }

  function closeCheckout(reason, payload) {
    // Close modal if open
    if (state.modalEl) {
      state.modalEl.style.display = "none";
      state.iframeEl.src = "about:blank";
    }

    // Close inline if open
    if (state.inlineEl && state.inlineContainer) {
      state.inlineContainer.style.display = "none";
      state.inlineContainer.innerHTML = "";
      state.inlineEl = null;
      state.inlineContainer = null;
    }

    // Handle onCancel callback for user-initiated closures
    if (reason === "user_closed" && state.callbacks.onCancel) {
      state.callbacks.onCancel({
        id: state.currentClientToken || null,
        reason: "user_closed",
        payload: payload || null,
      });
    }

    state.currentClientToken = null;
  }

  function createDefaultInlineContainer() {
    var container = document.createElement("div");
    container.id = "moment-inline-container";
    container.style.width = "100%";
    container.style.maxWidth = "500px";
    container.style.height = "600px";
    container.style.border = "1px solid #ccc";
    container.style.borderRadius = "8px";
    container.style.margin = "20px auto";
    container.style.backgroundColor = "#fff";
    container.style.overflow = "hidden";
    container.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";

    document.body.appendChild(container);
    return container;
  }

  function findOrCreateInlineContainer(container) {
    var containerEl;

    if (container) {
      // Use provided container
      containerEl =
        typeof container === "string"
          ? document.querySelector(container)
          : container;
      if (!containerEl) {
        throw new Error(
          "[Moment] Specified container element not found: " + container,
        );
      }
    } else {
      // Look for default container
      containerEl = document.querySelector("#moment-inline-container");

      if (!containerEl) {
        // Auto-create default container
        containerEl = createDefaultInlineContainer();
      }
    }

    return containerEl;
  }

  function openInline(checkoutUrl, clientToken, container) {
    var containerEl = findOrCreateInlineContainer(container);

    // Clear existing content
    containerEl.innerHTML = "";

    var iframe = document.createElement("iframe");
    iframe.id = "moment-collect-inline-iframe";
    iframe.style.border = "none";
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.setAttribute("allow", "payment *;");

    iframe.src = buildEmbedUrl(checkoutUrl);
    containerEl.appendChild(iframe);
    containerEl.style.display = "block";

    state.inlineEl = iframe;
    state.inlineContainer = containerEl;
    state.currentClientToken = clientToken;

    setupMessageListener();
  }

  function buildEmbedUrl(checkoutUrl) {
    var hasQuery = checkoutUrl.indexOf("?") >= 0;
    var url = checkoutUrl + (hasQuery ? "&" : "?") + "embed=1";

    // Add ancestor origins if present
    var ancestors = getAncestorOrigins();
    if (ancestors.length > 0) {
      url += "&ancestorOrigins=" + encodeURIComponent(ancestors.join(","));
    }
    console.log(url);
    return url;
  }

  function setupMessageListener() {
    if (state.messageListenerBound) return;
    window.addEventListener("message", function (event) {
      var data = event.data || {};
      if (!data || data.source !== "moment-checkout") {
        return;
      }

      var ev = data.event;
      // ========== TRUST IFRAME PAYLOAD DIRECTLY ==========
      // No backend re-fetch - use payload from postMessage as-is
      var payload = data.data || {};
      console.log(payload);
      if (ev === "checkout.completed") {
        closeCheckout("completed", payload);
        if (state.callbacks.onSuccess) {
          state.callbacks.onSuccess(payload);
        }
      } else if (ev === "checkout.failed") {
        closeCheckout("failed", payload);
        if (state.callbacks.onFailure) {
          state.callbacks.onFailure(payload);
        }
      } else if (ev === "checkout.attempt_failed") {
        if (state.callbacks.onAttemptFailed) {
          state.callbacks.onAttemptFailed(payload);
        }
      } else if (ev === "checkout.cancelled") {
        if (state.callbacks.onCancel) {
          state.callbacks.onCancel(payload);
        }
      } else if (ev === "checkout.expired") {
        closeCheckout("expired", payload);
        if (state.callbacks.onExpired) {
          state.callbacks.onExpired(payload);
        }
      } else if (ev === "checkout.redirect") {
        // Internal redirect handler - redirects user to the provided URL
        if (payload.redirect_url) {
          window.location.href = payload.redirect_url;
        }
      } else if (ev === "checkout.close") {
        closeCheckout("user_closed", payload);
      }
      // else if (ev === 'checkout.resize') {
      //   // Handle iframe resize requests
      //   var width = payload.width;
      //   var height = payload.height;

      //   if (state.modalEl && state.iframeEl) {
      //     // Resize modal container with fade transition
      //     var modalContainer = state.modalEl.querySelector('#moment-collect-modal');
      //     if (modalContainer) {
      //       // Ensure transition is set
      //       modalContainer.style.transition = 'opacity 0.1s ease-in-out';

      //       // Fade out
      //       modalContainer.style.opacity = '0';

      //       setTimeout(function() {
      //         // Apply new dimensions
      //         if (width) {
      //           modalContainer.style.maxWidth = width + 'px';
      //         }
      //         if (height) {
      //           state.iframeEl.style.height = height + 'px';
      //         }

      //         // Fade back in
      //         modalContainer.style.opacity = '1';
      //       }, 200);
      //     }
      //   }

      //   if (state.inlineEl && state.inlineContainer) {
      //     // Resize inline container and iframe
      //     if (width) {
      //       state.inlineContainer.style.width = width + 'px';
      //       state.inlineEl.style.width = width + 'px';
      //     }
      //     if (height) {
      //       state.inlineContainer.style.height = height + 'px';
      //       state.inlineEl.style.height = height + 'px';
      //     }
      //   }
      // }
      else {
        console.log("Unknown event from iframe:", ev);
        console.log("Callback handler not covered");
      }
    });

    state.messageListenerBound = true;
  }

  var Moment = {
    init: function (config) {
      if (!config || !config.clientKey) {
        throw new Error("[Moment] clientKey is required in init(config)");
      }

      if (
        !config.fetchClientToken ||
        typeof config.fetchClientToken !== "function"
      ) {
        throw new Error(
          "[Moment] fetchClientToken function is required in init(config)",
        );
      }

      var env = resolveEnvironment(config.clientKey);

      state.clientKey = config.clientKey;
      state.fetchClientToken = config.fetchClientToken;
      state.fetchSessionStatus = config.fetchSessionStatus || null;
      state.env = env;
      state.config = { ...DEFAULT_CONFIG, ...(config || {}) };
      state.callbacks.onSuccess = config.onSuccess || null;
      state.callbacks.onFailure = config.onFailure || null;
      state.callbacks.onCancel = config.onCancel || null;
      state.callbacks.onAttemptFailed = config.onAttemptFailed || null;
      state.callbacks.onExpired = config.onExpired || null;
      state.initialized = true;

      // Optional: log env for debugging
      if (env.type === "test") {
        console.log("[Moment] Initialized in", env.type, "environment");
      }

      // Check for session_id in URL after a redirect-based payment flow
      var sessionId = getSessionIdFromUrl();
      if (sessionId) {
        if (!state.fetchSessionStatus) {
          console.warn(
            "[Moment] session_id found in URL but no fetchSessionStatus callback provided. Skipping status check.",
          );
          removeSessionIdFromUrl();
        } else {
          Promise.resolve()
            .then(function () {
              return state.fetchSessionStatus(sessionId);
            })
            .then(function (response) {
              handleSessionStatus(response);
              removeSessionIdFromUrl();
            })
            .catch(function (error) {
              console.error("[Moment] fetchSessionStatus error:", error);
              removeSessionIdFromUrl();
            });
        }
      }
    },

    /**
     * Open a payment checkout in embedded mode (modal or inline).
     * Calls the fetchClientToken callback (provided in init) to retrieve a fresh JWT,
     * then decodes it to extract session_url and variant.
     * options: {
     *   container?: string | Element (only for inline variant)
     * }
     */
    open: function (options) {
      options = options || {};
      ensureInitialized();

      return Promise.resolve()
        .then(function () {
          return state.fetchClientToken();
        })
        .then(function (clientToken) {
          if (typeof clientToken !== "string" || !clientToken) {
            throw new Error(
              "[Moment] fetchClientToken must resolve to a non-empty string",
            );
          }

          // ========== JWT DECODING & VALIDATION ==========
          var payload = decodeJwt(clientToken);
          assertTokenNotExpired(payload);

          // Extract session_url directly from the JWT payload
          var checkoutUrl = payload.session_url;
          if (!checkoutUrl) {
            throw new Error("[Moment] session_url missing from clientToken");
          }

          // Determine variant: 'modal' or 'inline' (from JWT payload)
          var variant = payload.variant || "modal";

          if (variant === "modal") {
            openModal(checkoutUrl, clientToken);
          } else if (variant === "inline") {
            openInline(checkoutUrl, clientToken, options.container);
          } else {
            throw new Error(
              "[Moment] Invalid variant: " +
                variant +
                ". Expected modal or inline.",
            );
          }

          return { checkoutUrl: checkoutUrl, variant: variant };
        });
    },

    /**
     * Manually close the modal or inline checkout, if open.
     */
    close: function () {
      closeCheckout("manual_close");
    },

    /**
     * Trigger payment submission in the checkout iframe.
     * Only available for inline variant - not supported in modal.
     */
    submit: function () {
      ensureInitialized();

      // Submit is only available for inline variant
      if (!state.inlineEl) {
        console.warn(
          "[Moment] submit() is only available for inline variant, not modal",
        );
        return;
      }

      if (!state.inlineEl.contentWindow) {
        console.warn("[Moment] No active inline checkout iframe to submit");
        return;
      }

      // Send postMessage to iframe
      state.inlineEl.contentWindow.postMessage(
        {
          source: "moment-sdk",
          event: "checkout.submit",
        },
        "*",
      );

      console.log("[Moment] Submit triggered");
    },
  };

  window.Moment = Moment;
})(window);
